import { ReactNode } from 'react';

/**
 * Supported file type categories
 */
export type FileType = 'pdf' | 'image' | 'unsupported';

/**
 * Supported MIME types
 */
export const SUPPORTED_MIME_TYPES = {
  PDF: 'application/pdf',
  JPEG: 'image/jpeg',
  JPG: 'image/jpg',
  PNG: 'image/png',
  WEBP: 'image/webp',
} as const;

/**
 * File extensions mapped to file types
 */
export const FILE_EXTENSION_MAP = {
  '.pdf': 'pdf',
  '.jpg': 'image',
  '.jpeg': 'image',
  '.png': 'image',
  '.webp': 'image',
} as const;

/**
 * State for the file preview
 */
export interface FilePreviewState {
  isOpen: boolean;
  fileUrl: string | null;
  fileType: FileType | null;
  fileName: string | null;
}

/**
 * Context value provided by FilePreviewProvider
 */
export interface FilePreviewContextType extends FilePreviewState {
  /**
   * Opens the file preview with the given URL
   * @param url - HTTP/HTTPS URL of the file to preview
   * @param fileName - Optional display name for the file
   */
  open: (url: string, fileName?: string) => void;

  /**
   * Closes the file preview and cleans up resources
   */
  close: () => void;

  /**
   * Sets or updates the file URL without opening the viewer
   * Useful for preloading or updating the URL
   */
  setFileUrl: (url: string | null) => void;

  /**
   * Checks if a given file type is supported
   * @param url - URL to check (extracts extension)
   * @returns true if the file type is supported
   */
  isSupported: (url: string) => boolean;
}

/**
 * Props for FilePreviewProvider
 */
export interface FilePreviewProviderProps {
  children: ReactNode;
}

/**
 * Props for FilePreview component
 */
export interface FilePreviewProps {
  /**
   * Additional CSS classes for the container
   */
  className?: string;

  /**
   * Show loading state
   */
  loading?: boolean;

  /**
   * Custom loading component
   */
  loadingComponent?: ReactNode;

  /**
   * Custom error component for unsupported files
   */
  unsupportedComponent?: ReactNode;
}
